import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChatRoom, SendMessageRequest } from 'amazon-ivs-chat-messaging';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import CommunityChatPanel from './CommunityChatPanel';
import PinnedMessagesPopover from './PinnedMessagesPopover';
import MembersDrawer from './MembersDrawer';
import ChatRail from './ChatRail';
import communityApi from '../../services/communityApi';
import {
  authApi,
  clearStoredAuth,
  readStoredToken,
  readStoredUser as readStoredAuthUser,
} from '../../services/authApi';
import { ROUTES } from '../../constants';
import { hasDirectAdminAccess } from '../../utils/adminAccess';
import './CommunityPage.css';

const PER_USER_CHATS_ENABLED =
  String(process.env.REACT_APP_ENABLE_PER_USER_CHATS).toLowerCase() === 'true';

function readStoredUser() {
  return readStoredAuthUser() || {};
}

function CommunityPage() {
  const navigate = useNavigate();
  const { ownerUsername } = useParams();
  const roomId = ownerUsername ? `user:${ownerUsername}` : 'all';

  const storedUser = useMemo(readStoredUser, []);
  const currentUser = useMemo(
    () => ({
      id: storedUser.id || '',
      username: storedUser.username || '',
      name: storedUser.username || storedUser.firstName || 'You',
      avatar: storedUser.profileImage || '',
      email: storedUser.email || '',
    }),
    [storedUser],
  );

  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [pinnedLoading, setPinnedLoading] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [chatError, setChatError] = useState('');
  const [connectionState, setConnectionState] = useState('disconnected');
  const [capabilities, setCapabilities] = useState([]);
  const [spadesBalance, setSpadesBalance] = useState(0);
  const [remoteChats, setRemoteChats] = useState([]);

  const roomRef = useRef(null);
  const displayNameRef = useRef(currentUser.name);
  const profileImageRef = useRef(currentUser.avatar || '');

  const activeChat = useMemo(
    () => (ownerUsername ? { type: 'user', owner: ownerUsername } : { type: 'all' }),
    [ownerUsername],
  );
  const isAdmin = useMemo(
    () => hasDirectAdminAccess() || capabilities.includes('DELETE_MESSAGE'),
    [capabilities],
  );

  useEffect(() => {
    const token = readStoredToken();
    if (!token) return;
    authApi
      .getPaymentBalance(undefined, token)
      .then((data) => setSpadesBalance(Number(data?.spade_balance) || 0))
      .catch(() => {});
  }, []);

  const loadChats = useCallback(async () => {
    if (!PER_USER_CHATS_ENABLED) return;
    try {
      const data = await communityApi.listChats();
      setRemoteChats(data?.chats || []);
    } catch (err) {
      console.warn('Failed to load chat list:', err);
    }
  }, []);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  const loadPinned = useCallback(async () => {
    setPinnedLoading(true);
    try {
      const data = await communityApi.listPinnedMessages(roomId);
      setPinned(data.pinned || []);
    } catch (err) {
      console.error('Failed to load pinned messages:', err);
    } finally {
      setPinnedLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    setMessages([]);
    setPinned([]);
    setMembers([]);
    setMembersTotal(0);
    setCapabilities([]);
    setChatError('');
    setPinnedOpen(false);
    setMembersOpen(false);
    loadPinned();
  }, [loadPinned]);

  useEffect(() => {
    let cancelled = false;
    let room = null;
    let cleanups = [];

    const tokenProvider = async () => {
      const resp = await communityApi.issueChatToken(roomId);
      if (resp.displayName) displayNameRef.current = resp.displayName;
      if (resp.profileImage) profileImageRef.current = resp.profileImage;
      if (Array.isArray(resp.capabilities)) setCapabilities(resp.capabilities);
      return {
        token: resp.token,
        sessionExpirationTime: resp.sessionExpirationTime
          ? new Date(resp.sessionExpirationTime)
          : undefined,
        tokenExpirationTime: resp.tokenExpirationTime
          ? new Date(resp.tokenExpirationTime)
          : undefined,
      };
    };

    (async () => {
      try {
        const first = await communityApi.issueChatToken(roomId);
        if (cancelled) return;
        if (first.displayName) displayNameRef.current = first.displayName;
        if (first.profileImage) profileImageRef.current = first.profileImage;
        if (Array.isArray(first.capabilities)) setCapabilities(first.capabilities);
        if (roomId !== 'all') loadChats();

        room = new ChatRoom({
          regionOrUrl: first.region || 'us-east-1',
          tokenProvider,
        });
        roomRef.current = room;

        cleanups.push(room.addListener('connecting', () => setConnectionState('connecting')));
        cleanups.push(
          room.addListener('connect', () => {
            setConnectionState('connected');
            setChatError('');
            communityApi
              .fetchHistory(roomId, { limit: 50 })
              .then((resp) => {
                if (cancelled) return;
                const historical = (resp?.messages || []).map((h) => ({
                  id: h.id,
                  content: h.content,
                  sendTime: h.sentAt ? new Date(h.sentAt) : undefined,
                  sender: { userId: h.senderUserId || undefined },
                  attributes: {
                    displayName: h.senderDisplayName || undefined,
                    profileImage: h.senderProfileImage || undefined,
                  },
                }));
                setMessages((prev) => {
                  const seen = new Set(prev.map((m) => m.id));
                  const merged = [...historical.filter((h) => !seen.has(h.id)), ...prev];
                  return merged;
                });
              })
              .catch((err) => console.warn('Failed to load chat history:', err));
          }),
        );
        cleanups.push(
          room.addListener('disconnect', (reason) => {
            setConnectionState('disconnected');
            if (reason !== 'clientDisconnect') {
              setChatError(`Connection lost (${reason}). Reconnecting…`);
            }
          }),
        );
        cleanups.push(
          room.addListener('message', (message) => {
            setMessages((prev) => [...prev, message]);
          }),
        );
        cleanups.push(
          room.addListener('messageDelete', (event) => {
            setMessages((prev) => prev.filter((m) => m.id !== event.messageId));
          }),
        );
        cleanups.push(
          room.addListener('event', (event) => {
            if (event.eventName === 'PIN_ADDED' || event.eventName === 'PIN_REMOVED') {
              loadPinned();
            }
          }),
        );

        room.connect();
      } catch (err) {
        console.error('Failed to connect to community chat:', err);
        if (!cancelled) {
          setChatError(err.message || 'Unable to connect to community chat.');
        }
      }
    })();

    return () => {
      cancelled = true;
      cleanups.forEach((off) => {
        try {
          off();
        } catch {}
      });
      if (room) {
        try {
          room.disconnect();
        } catch {}
      }
      roomRef.current = null;
    };
  }, [loadPinned, loadChats, roomId]);

  const handleSend = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || !roomRef.current) return;
    try {
      const attributes = { displayName: displayNameRef.current || 'User' };
      if (profileImageRef.current) attributes.profileImage = profileImageRef.current;
      await roomRef.current.sendMessage(new SendMessageRequest(trimmed, attributes));
    } catch (err) {
      console.error('Failed to send message:', err);
      setChatError(err?.errorMessage || err?.message || 'Failed to send message.');
    }
  }, []);

  const handlePinMessage = useCallback(
    async (message) => {
      if (!isAdmin || !message) return;
      try {
        await communityApi.pinMessage(roomId, {
          messageId: message.id,
          content: message.content,
          senderUserId: message.sender?.userId || null,
          senderDisplayName:
            message.attributes?.displayName || message.sender?.userId || null,
        });
        loadPinned();
      } catch (err) {
        console.error('Failed to pin message:', err);
        alert(err?.message || 'Failed to pin message.');
      }
    },
    [isAdmin, loadPinned, roomId],
  );

  const handleUnpinMessage = useCallback(
    async (messageId) => {
      if (!isAdmin || !messageId) return;
      try {
        await communityApi.unpinMessage(roomId, messageId);
        loadPinned();
      } catch (err) {
        console.error('Failed to unpin message:', err);
      }
    },
    [isAdmin, loadPinned, roomId],
  );

  const handleTogglePinned = useCallback(() => {
    setMembersOpen(false);
    setPinnedOpen((prev) => {
      const next = !prev;
      if (next) loadPinned();
      return next;
    });
  }, [loadPinned]);

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const data = await communityApi.listMembers(roomId);
      setMembers(data.members || []);
      setMembersTotal(typeof data.total === 'number' ? data.total : (data.members || []).length);
    } catch (err) {
      console.error('Failed to load members:', err);
    } finally {
      setMembersLoading(false);
    }
  }, [roomId]);

  const handleLeaveUserChat = useCallback(async () => {
    if (!ownerUsername) return;
    if (currentUser.username && currentUser.username === ownerUsername) return;
    const confirmed = window.confirm(`Leave @${ownerUsername}'s chat?`);
    if (!confirmed) return;
    try {
      await communityApi.leaveUserChat(ownerUsername);
      setMembersOpen(false);
      navigate('/community');
      loadChats();
    } catch (err) {
      console.error('Failed to leave chat:', err);
      alert(err?.message || 'Failed to leave chat.');
    }
  }, [ownerUsername, currentUser.username, navigate, loadChats]);

  const handleToggleMembers = useCallback(() => {
    setPinnedOpen(false);
    setMembersOpen((prev) => {
      const next = !prev;
      if (next) loadMembers();
      return next;
    });
  }, [loadMembers]);

  const normalizedMessages = useMemo(
    () =>
      messages.map((m) => {
        const isMine = m.sender?.userId === currentUser.id;
        const profileImage =
          m.attributes?.profileImage
          || (isMine ? currentUser.avatar : null)
          || null;
        return {
          id: m.id,
          content: m.content,
          timestamp: m.sendTime instanceof Date ? m.sendTime.toISOString() : m.sendTime,
          sender: {
            username:
              m.attributes?.displayName
              || (isMine ? currentUser.username : m.sender?.userId)
              || 'unknown',
            profileImage,
          },
          _isMine: isMine,
          _raw: m,
        };
      }),
    [messages, currentUser.id, currentUser.username, currentUser.avatar],
  );

  const chats = useMemo(() => {
    const base = [{ id: 'all', type: 'all', owner: null }];
    const seen = new Set();
    const pushUserChat = (owner, ownerAvatar) => {
      if (!owner || seen.has(owner)) return;
      seen.add(owner);
      base.push({ id: `user:${owner}`, type: 'user', owner, ownerAvatar });
    };
    if (PER_USER_CHATS_ENABLED) {
      for (const c of remoteChats) {
        if (c?.type === 'user') pushUserChat(c.ownerUsername, c.ownerAvatar);
      }
    }
    if (ownerUsername) pushUserChat(ownerUsername, undefined);
    return base;
  }, [ownerUsername, remoteChats]);

  const headerTitle = ownerUsername ? `@${ownerUsername}'s Chat` : 'All Chat';

  return (
    <div className="community-page">
      <HomeNavBar
        spadesBalance={spadesBalance}
        notificationCount={0}
        userAvatar={currentUser.avatar}
        userName={currentUser.name}
        onSpadesClick={() => {}}
        onLogout={async () => {
          await authApi.logout();
          clearStoredAuth();
          navigate(ROUTES.WELCOME);
        }}
      />

      <LeftSidebar />

      <div className="community-page__body">
        <ChatRail
          chats={chats}
          activeChat={activeChat}
          loading={false}
          onSelect={(chat) => {
            if (!chat) return;
            if (chat.type === 'all') {
              navigate('/community');
            } else if (chat.type === 'user' && chat.owner) {
              navigate(`/community/user/${encodeURIComponent(chat.owner)}`);
            }
          }}
        />

        <div className="community-page__chat-wrapper">
          <CommunityChatPanel
            title={headerTitle}
            connectionState={connectionState}
            messages={normalizedMessages}
            error={chatError}
            currentUser={currentUser}
            isAdmin={isAdmin}
            onSend={handleSend}
            onTogglePinned={handleTogglePinned}
            onToggleMembers={handleToggleMembers}
            onPinMessage={handlePinMessage}
          />

          {pinnedOpen && (
            <PinnedMessagesPopover
              pinned={pinned}
              loading={pinnedLoading}
              canUnpin={isAdmin}
              onClose={() => setPinnedOpen(false)}
              onUnpin={handleUnpinMessage}
            />
          )}

          {membersOpen && (
            <MembersDrawer
              total={membersTotal}
              members={members}
              loading={membersLoading}
              onClose={() => setMembersOpen(false)}
              currentUsername={currentUser.username}
              ownerUsername={ownerUsername || undefined}
              canLeave={
                !!ownerUsername && currentUser.username !== ownerUsername
              }
              onLeave={handleLeaveUserChat}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CommunityPage;
